import { Country } from "@utils/constants/localisation";
import { IMSTokenParams, getWithIDToken, postWithIdToken } from "@utils/http/MSAuthAPI";
import {AddonCharge} from "@views/VehicleExchange/additionalChargesReducer";
import {ExchangeTypes} from "@views/VehicleExchange/exchangeReasonReducer";
import {logger as SentryLogger} from '@sentry/react';

// Types for request/response
// VehicleExchangeFields interface
export interface VehicleExchangeDocumentFields {
    document_type: 'vehicle_exchange';
    exchange_type: ExchangeTypes;
    exchange_reason: string;
    exchange_comments: string;
    current_vehicle_id: string;
    current_vehicle_ending_odometer: number;
    current_vehicle_desired_status_id: number;
    // GB only: the Fleetio `vehicle_location` the returning vehicle is being left at.
    // Absent for AU, which models location through Fleetio groups instead (TP-6849).
    current_vehicle_return_location?: string;
    new_vehicle_id: string;
    new_vehicle_starting_odometer: number;
    subscription_id: string;
    additional_charges?: AddonCharge[];
    // Price change only
    new_plan_id?: string;
    new_coupon_id?: string | null;
    // Minor currency (pence/cents) - the reducer holds these in minor already
    new_weekly_fee?: number;
    current_weekly_fee?: number | null;
}

// SubscriptionContract is not currently supported, but included as guide for future
export interface SubscriptionContractDocumentFields {
    document_type: 'subscription_contract';
    // additional fields here
}

// QuoteAddon interface for FO V2
export interface QuoteAddon {
    plan_item_type: string;  // "Plan", "Addon", or "Coupon"
    plan_item_key: string;   // Full Chargebee item ID
    plan_component: string;  // Short component name
    quantity: number;
    // Pricing fields for Zoho Sign template prefill (GB and AU)
    line_item_label_code?: string | null;
    price_excluding_gst?: number | null;
    price_including_gst?: number | null;
    included?: boolean | null;
}

// FlexiOwnV2SubscriptionContractFields interface
export interface FlexiOwnV2SubscriptionContractFields {
    document_type: 'flexi_own_v2_subscription_contract';

    // Core identifiers
    contact_id: string;
    vehicle_id: string;
    potential_id?: string | null;

    // Plan details
    plan: string;
    plan_id: string;
    plan_length?: string | null;

    // Dates
    start_date: string; // ISO format

    // Pricing
    pricing_record_id: string;
    weekly_fee: string;
    setup_fee?: string | null;

    // Addons
    quote_addons: QuoteAddon[];

    // Vehicle
    odometer: string;
    colour: string;

    // Customer
    email: string;
    mobile?: string | null;

    // Customer personal details (optional overrides for incomplete Zoho data)
    first_name?: string | null;
    middle_name?: string | null;
    last_name?: string | null;
    gender?: string | null;
    nationality?: string | null;
    date_of_birth?: string | null; // ISO format YYYY-MM-DD
    driver_licence_number?: string | null;
    driver_licence_expiry?: string | null; // ISO format YYYY-MM-DD
    national_insurance_number?: string | null;
    pco_licence_number?: string | null;
    pco_licence_expiry?: string | null;

    // Address details (optional overrides for incomplete Zoho data)
    street?: string | null;
    city?: string | null;
    state?: string | null;
    postcode?: string | null;

    // Emergency contacts
    primary_emergency_name?: string | null;
    primary_emergency_phone?: string | null;
    primary_emergency_address?: string | null;
    primary_emergency_relationship?: string | null;

    secondary_emergency_name?: string | null;
    secondary_emergency_phone?: string | null;
    secondary_emergency_address?: string | null;
    secondary_emergency_relationship?: string | null;

    // AU-specific fields
    hirer_abn?: string | null;
    toll_tag_number?: string | null;
    toll_tag_account_number?: string | null;
    toll_tag_issuer?: string | null;
    driver_licence_state?: string | null;

    // Pricing fields for Zoho Sign template (GB and AU)
    weekly_distance_allowance?: string | null;
    excess_distance_cost?: string | null;
    loss_and_damage_waiver_value?: string | null;
    a1_total_value?: string | null;
    a2_total_value?: string | null;
    a3_total_value?: string | null;
    total_addons_value?: string | null;
    reduced_term_length_weeks?: number | null;
    term_reduction?: string | null;
    vehicle_driveaway_price?: string | null;
    // AU NSW EV Addendum
    nsw_total_incentive_excl_gst?: string | null;
    // AU Zoho subscription fields
    plan_minimum_period?: number | null;

    // Customer Zoho fields
    alternate_name?: string | null;
    contact_owner_id?: string | null;

    // Optional
    splend_initiative?: string | null;
    ipb_discount_amount?: number;
    ipb_discounted_weekly_subscription?: number;
    insurance_doc_store_id?: string | null;

    // Used vehicle (pre-loved) addendum fields
    replacement_tyres?: string | null;
    replacement_brake_pads?: string | null;
    brake_or_machined_discs?: string | null;
}

// Type with specific fields for the types of signature documents
export type SignatureDocumentJourneyFields = VehicleExchangeDocumentFields | SubscriptionContractDocumentFields | FlexiOwnV2SubscriptionContractFields;

// Top level request - common fields for all signature documents + the document specific fields in subtype
export interface NewSignatureDocumentRequest {
    customer_zoho_contact_id: string;
    customer_email?: string;
    country: Country;
    csr_name?: string | null;
    csr_email?: string | null;
    document: SignatureDocumentJourneyFields;
}

export interface NewSignatureDocumentResponse {
    document_id: string;
}


export interface SignatureDocumentDetailsResponse {
    embed_sign_url?: string;
}

interface ISignatureDocumentService {
    createSignatureDocument(
        body: NewSignatureDocumentRequest,
        IDTokenParams: IMSTokenParams
    ): Promise<NewSignatureDocumentResponse>;
    getSignatureDocumentDetails(
        documentId: string,
        country: Country,
        IDTokenParams: IMSTokenParams,
        embedSignRole?: string
    ): Promise<SignatureDocumentDetailsResponse>;
}

export const SignatureDocumentService: ISignatureDocumentService = {
    async createSignatureDocument(body, IDTokenParams) {
        const { country, ...rest } = body;
        SentryLogger.debug("Sending create-signature-document request with body:", { country, rest });
        const resp = await postWithIdToken(
            "v1/signature-document",
            IDTokenParams,
            { country }, // query params
            rest // payload (all except country)
        );
        SentryLogger.info("Signature document created", { country: country, request: rest, response: resp.data });
        return resp.data;
    },
    async getSignatureDocumentDetails(documentId, country, IDTokenParams, embedSignRole) {
        const queryParams: any = { country };
        if (embedSignRole) queryParams.embed_sign_role = embedSignRole;
        SentryLogger.debug("Fetching signature document details for documentId:", { documentId, queryParams });
        const resp = await getWithIDToken(
            `v1/signature-document/${encodeURIComponent(documentId)}`,
            IDTokenParams,
            queryParams
        );
        SentryLogger.info("Signature document details fetched", { documentId: documentId, country: country, response: resp.data });
        return resp.data;
    }
};
